import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { tagClass, statusName, quotaVal, industryLabel } from '../lib/tenant';
import { Icons } from '../components/icons';

export function HealthMonitoring() {
  const [services, setServices] = useState([]);
  const [infra, setInfra] = useState([]);
  const [summary, setSummary] = useState({ up: 0, down: 0, total: 0 });
  const [infraSummary, setInfraSummary] = useState({ up: 0, down: 0, warn: 0, total: 0 });
  const [gatewayVersion, setGatewayVersion] = useState('—');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState('');

  const checkServices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await apiFetch('/api/v1/platform/services/health');
      if (d.code !== 0) {
        setError(d.message || '加载失败');
        setServices([]);
        setInfra([]);
        setLoading(false);
        return;
      }
      const data = d.data || {};
      setServices(data.services || []);
      setInfra(data.infra || []);
      setSummary(data.summary || { up: 0, down: 0, total: 0 });
      setInfraSummary(data.infra_summary || { up: 0, down: 0, warn: 0, total: 0 });
      setGatewayVersion(data.gateway_version || '—');
      setLastRefresh(new Date(data.checked_at || Date.now()));
    } catch (e) {
      setError('无法连接网关: ' + (e.message || e));
      setServices([]);
      setInfra([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkServices(); const iv = setInterval(checkServices, 30000); return () => clearInterval(iv); }, [checkServices]);

  const upCount = summary.up ?? services.filter(s => s.status === 'up').length;
  const downCount = summary.down ?? services.filter(s => s.status === 'down').length;
  const totalCount = summary.total ?? services.length;
  const infraDot = (s) => s === 'up' ? 'green' : s === 'warn' ? 'orange' : 'red';

  return <div>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
      <h2 style={{fontSize:18,fontWeight:650}}>系统监控</h2>
      <button className="btn btn-outline btn-xs" onClick={checkServices} disabled={loading}>
        <Icons.Refresh w="12" h="12"/> {loading?'检查中...':'刷新'}
      </button>
    </div>

    {error && <div className="card" style={{padding:12,marginBottom:16,borderColor:'#fecaca',background:'#fef2f2',color:'#b91c1c',fontSize:13}}>{error}</div>}

    <div className="health-summary">
      <div className="health-summary-item">
        <div className="hs-val" style={{color:'var(--brand-primary)'}}>{upCount}</div>
        <div className="hs-label">微服务正常</div>
      </div>
      <div className="health-summary-item">
        <div className="hs-val" style={{color:downCount>0?'#dc2626':'var(--brand-text-muted)'}}>{downCount}</div>
        <div className="hs-label">微服务异常</div>
      </div>
      <div className="health-summary-item">
        <div className="hs-val" style={{color:'#2563eb'}}>{infraSummary.up ?? 0}</div>
        <div className="hs-label">基础设施正常</div>
      </div>
      <div className="health-summary-item">
        <div className="hs-val" style={{color:(infraSummary.down||0)>0?'#dc2626':'var(--brand-text-muted)'}}>{infraSummary.down ?? 0}</div>
        <div className="hs-label">基础设施异常</div>
      </div>
      <div className="health-summary-item">
        <div className="hs-val" style={{fontSize:14}}>v{gatewayVersion}</div>
        <div className="hs-label">网关版本</div>
      </div>
      <div className="health-summary-item">
        <div className="hs-val" style={{fontSize:11}}>{lastRefresh ? new Date(lastRefresh).toLocaleTimeString() : '—'}</div>
        <div className="hs-label">最后检查</div>
      </div>
    </div>

    <div className="card-title" style={{marginTop:8}}>微服务</div>
    <p style={{fontSize:11,color:'var(--brand-text-muted)',marginBottom:12}}>由网关在本机探测各微服务 /health</p>
    <div className="health-grid" style={{marginBottom:24}}>
      {loading && services.length === 0
        ? [1,2,3,4,5,6].map(i => <div key={i} className="skeleton" style={{height:52,borderRadius:10}}/>)
        : services.map(s => <div key={s.key} className="health-card">
            <div className={`health-dot ${s.status==='up'?'green':'red'}`}/>
            <div>
              <div className="health-name">{s.name_cn || s.name}</div>
              <div className="health-sub">{s.name}{s.status==='up'
                ? ` · :${s.port}${s.version ? ' · ' + s.version : ''}`
                : ` · :${s.port} · ${s.error || '无响应'}`}</div>
            </div>
          </div>)
      }
    </div>

    <div className="card-title">基础设施</div>
    <p style={{fontSize:11,color:'var(--brand-text-muted)',marginBottom:12}}>
      {infra.length
        ? infra.map(s => s.name_cn || s.key).join(' / ') + ' · 端口 / HTTP / 磁盘探测'
        : '端口 / HTTP / 磁盘探测（名称由网关按实际组件上报）'}
    </p>
    <div className="health-grid">
      {loading && infra.length === 0
        ? [1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{height:52,borderRadius:10}}/>)
        : infra.map(s => <div key={s.key} className="health-card">
            <div className={`health-dot ${infraDot(s.status)}`}/>
            <div>
              <div className="health-name">{s.name_cn || s.key}</div>
              <div className="health-sub">
                {s.status === 'up' ? (s.detail || '正常')
                  : s.status === 'warn' ? (s.detail || '告警')
                  : (s.error || '不可用')}
                {s.free_gb != null ? ` · 剩余 ${s.free_gb} GB` : ''}
                {s.port ? ` · :${s.port}` : ''}
              </div>
            </div>
          </div>)
      }
    </div>
  </div>;
}

